import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { Download } from "lucide-react";
import { downloadCSV } from "@/lib/csv";

const AGE_LABEL = { primary: "Primary", teens: "Teens", k12: "K-12", ib_alevel_cpe: "IB/A-Level/Cambridge" };

export default function LeadsPanel() {
  const [items, setItems] = useState([]);
  useEffect(() => { (async () => {
    try { const { data } = await api.get("/leads"); setItems(data); } catch { /* ignore */ }
  })(); }, []);

  function copyEmails() {
    const emails = items.map((l) => l.email).join(", ");
    navigator.clipboard?.writeText(emails);
    toast.success(`${items.length} email(s) copied`);
  }

  function exportCSV() {
    if (items.length === 0) { toast.error("No leads to export"); return; }
    const rows = items.map((l) => ({
      created_at: format(parseISO(l.created_at), "yyyy-MM-dd HH:mm"),
      name: l.name,
      email: l.email,
      child_age_band: AGE_LABEL[l.child_age_band] || l.child_age_band,
      source: l.source,
    }));
    const stamp = format(new Date(), "yyyyMMdd-HHmm");
    downloadCSV(`dawnville-leads-${stamp}.csv`, rows, [
      { key: "created_at", label: "Created" },
      { key: "name", label: "Name" },
      { key: "email", label: "Email" },
      { key: "child_age_band", label: "Child stage" },
      { key: "source", label: "Source" },
    ]);
    toast.success(`Exported ${rows.length} lead(s)`);
  }

  return (
    <div data-testid="admin-leads-panel">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="text-xs uppercase tracking-[0.25em] text-[hsl(var(--muted-foreground))]">Reading Challenge subscribers · {items.length}</h3>
        {items.length > 0 && (
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={exportCSV} data-testid="admin-leads-export-csv" className="rounded-full"><Download className="w-3.5 h-3.5 mr-1.5" />Export CSV</Button>
            <Button size="sm" variant="outline" onClick={copyEmails} data-testid="admin-leads-copy" className="rounded-full">Copy all emails</Button>
          </div>
        )}
      </div>
      <div className="overflow-x-auto bg-white border border-[hsl(var(--border))] rounded-2xl">
        <table className="w-full text-sm" data-testid="admin-leads-table">
          <thead>
            <tr className="text-left text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))] border-b border-[hsl(var(--border))]">
              <th className="p-4 font-normal">When</th><th className="p-4 font-normal">Name</th><th className="p-4 font-normal">Email</th><th className="p-4 font-normal">Stage</th><th className="p-4 font-normal">Source</th>
            </tr>
          </thead>
          <tbody>
            {items.map((l) => (
              <tr key={l.id} data-testid={`admin-lead-row-${l.id}`} className="border-b border-[hsl(var(--border))]/60 last:border-0">
                <td className="p-4 text-[hsl(var(--muted-foreground))]">{format(parseISO(l.created_at), "MMM dd HH:mm")}</td>
                <td className="p-4">{l.name}</td>
                <td className="p-4 text-[hsl(var(--muted-foreground))]">{l.email}</td>
                <td className="p-4">{AGE_LABEL[l.child_age_band] || l.child_age_band}</td>
                <td className="p-4 text-[hsl(var(--muted-foreground))]">{l.source}</td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={5} className="p-10 text-center text-[hsl(var(--muted-foreground))]">No leads yet — promote the homepage Reading Challenge section.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
